import React, { useRef, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, PageLoader, Btn, EmptyState, brandName } from "@/lib/ui";
import { Images, Upload, Film, Music, FileText } from "lucide-react";

const KIND_ICON = { video: Film, audio: Music, pdf: FileText, document: FileText };

function MediaThumb({ m }) {
  const api = useApi();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    if (m.kind === "image") {
      api.blob(`/media/${m.id}/raw`).then((u) => { obj = u; setUrl(u); })
        .catch((e) => console.error("media load failed", e));
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id]);
  const Icon = KIND_ICON[m.kind] || FileText;
  return (
    <div className="aspect-square bg-surface-elevated flex items-center justify-center overflow-hidden">
      {m.kind === "image" && url
        ? <img src={url} alt={m.alt_text || m.filename} className="h-full w-full object-cover" />
        : <Icon className="h-8 w-8 text-muted-foreground" />}
    </div>
  );
}

export default function Media() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const fileRef = useRef(null);
  const { data: media = [], isLoading } = useQuery({ queryKey: ["media", brand], queryFn: () => api.get(`/media?brand_id=${brand}`) });

  const upload = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); fd.append("brand_id", brand); return api.upload("/media", fd); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["media"] }); toast.success("Media added"); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Library`} title="Media Library">
        <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files[0] && upload.mutate(e.target.files[0])} data-testid="media-file-input" />
        <Btn onClick={() => fileRef.current?.click()} disabled={upload.isPending} data-testid="media-upload-btn"><Upload className="h-4 w-4" /> Add Media</Btn>
      </PageHeader>

      {media.length === 0 ? (
        <EmptyState icon={Images} title="No media for this brand yet" hint="Upload images, video, audio or PDFs with tags, alt text and copyright source. Stored in durable cloud storage." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {media.map((m) => (
            <Panel key={m.id} className="overflow-hidden" data-testid={`media-${m.id}`}>
              <MediaThumb m={m} />
              <div className="p-2">
                <div className="text-xs truncate">{m.filename}</div>
                <div className="text-[10px] font-mono text-muted-foreground uppercase">{m.kind}</div>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
